/** Connector-specific ports for the reused agent-desktop SDK (adr-039): the edge
 *  REST backend (/api/edge/agent/api/*), the LiveKit media controller, and the
 *  poll source. These are the only network-touching pieces; the client + reducer
 *  are the console's proven code, copied verbatim. */
import { ParticipantKind, RemoteAudioTrack, Room, RoomEvent, Track, type RemoteParticipant, type RemoteTrackPublication } from "livekit-client";

import type { AgentBackend, Availability, InteractionRef, MediaController, MediaHooks, PollSource } from "@ccv2/agent-sdk";

import { loadSettings } from "../state/settingsStore";

async function post(url: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const r = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return (await r.json().catch(() => ({}))) as Record<string, unknown>;
}

/** REST commands against the edge agent-desktop BFF. `base` = …/agent/api,
 *  `edgeBase` = … (transfer/conference live under /calls). */
export class EdgeAgentBackend implements AgentBackend {
  constructor(private base: string, private edgeBase: string, private agentId: string) {}
  async dial(destination: string) {
    const r = await post(`${this.edgeBase}/cti/dial`, { to: destination, identity: this.agentId }) as
      { url?: string; token?: string; room?: string; call_id?: string; originated?: boolean; error?: string | null };
    return { url: r.url, token: r.token, room: r.room, call_id: r.call_id,
             error: r.originated === false ? (r.error || "call could not be originated") : undefined };
  }
  answer(ix: string) { return post(`${this.base}/answer`, { agent_id: this.agentId, interaction_id: ix }); }
  reject(ix: string) { return post(`${this.base}/reject`, { agent_id: this.agentId, interaction_id: ix }).then(() => {}); }
  hold(ix: string, room: string) { return post(`${this.base}/hold`, { agent_id: this.agentId, interaction_id: ix, room_name: room }).then(() => {}); }
  resume(ix: string) { return post(`${this.base}/resume`, { agent_id: this.agentId, interaction_id: ix }).then(() => {}); }
  hangup(ix: string, room: string) { return post(`${this.base}/hangup`, { agent_id: this.agentId, interaction_id: ix, room_name: room }).then(() => {}); }
  disconnect(ix: string, reason: string) { return post(`${this.base}/disconnect`, { agent_id: this.agentId, interaction_id: ix, reason_code: reason }).then(() => {}); }
  setState(state: Availability, reason?: string | null) { return post(`${this.base}/state`, { agent_id: this.agentId, state, reason_code: reason ?? null }).then(() => {}); }
  pciPause(ix: string) { return post(`${this.base}/pci-pause`, { agent_id: this.agentId, interaction_id: ix }).then(() => {}); }
  pciResume(ix: string) { return post(`${this.base}/pci-resume`, { agent_id: this.agentId, interaction_id: ix }).then(() => {}); }
  transfer(ix: string, target: string, kind: "agent" | "queue" | "external", mode: "blind" | "warm") {
    return post(`${this.edgeBase}/calls/${ix}/transfer`, { agent_id: this.agentId, target, target_kind: kind, mode }).then(() => {});
  }
  conference(ix: string, participants: string[]) { return post(`${this.edgeBase}/calls/${ix}/conference`, { agent_id: this.agentId, participants }).then(() => {}); }
  wrapup(ix: string, disposition: string, notes: string) { return post(`${this.base}/wrapup`, { agent_id: this.agentId, interaction_id: ix, disposition, notes }).then(() => {}); }
  abandonWrapup(ix: string, disposition: string, notes: string) { return post(`${this.base}/abandon-wrapup`, { agent_id: this.agentId, interaction_id: ix, disposition, notes }).then(() => {}); }
}

function isCaller(p: RemoteParticipant): boolean {
  try { if (ParticipantKind && p.kind === ParticipantKind.SIP) return true; } catch { /* older SDK */ }
  if ((p as unknown as { kind?: number }).kind === 3) return true;
  const id = String(p?.identity ?? "");
  return id !== "" && !/^(agent-|supervisor-|ccv2-)/.test(id);
}

/** LiveKit room lifecycle for the answered inbound call (SDK-driven). */
export class LiveKitMedia implements MediaController {
  private room: Room | null = null;
  /** The elements playing the call, kept so HOLD can silence them. */
  private audio = new Map<string, HTMLMediaElement>();
  /* Mute and hold are tracked apart because they undo apart: Resume must not
     open the microphone of an agent who muted themselves, and un-muting must
     not open the microphone of an agent still on hold. */
  private muted = false;
  private held = false;
  async connect(creds: { url: string; token: string }, hooks: MediaHooks): Promise<{ room?: string }> {
    if (this.room) { try { await this.room.disconnect(); } catch { /* ignore */ } this.room = null; }
    const room = new Room();
    room.on(RoomEvent.Disconnected, () => hooks.onRemoteEnded());
    room.on(RoomEvent.TrackSubscribed, (t, _pub: RemoteTrackPublication) => {
      if (t.kind !== Track.Kind.Audio) return;
      const el = (t as RemoteAudioTrack).attach();
      el.muted = this.held;                    // the music bot publishes mid-hold
      this.audio.set(t.sid ?? String(this.audio.size), el);
    });
    room.on(RoomEvent.TrackUnsubscribed, (t) => { if (t.sid) this.audio.delete(t.sid); });
    room.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => { if (isCaller(p)) hooks.onRemoteConnected?.(); });
    room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => { if (isCaller(p)) hooks.onRemoteEnded(); });
    await room.connect(creds.url, creds.token);
    this.muted = false; this.held = false;     // a fresh call starts open
    await room.localParticipant.setMicrophoneEnabled(true);
    this.room = room;
    await this.applyDevices();   // honour the agent's saved mic/speaker choice
    // Outbound: the callee's SIP leg may already be present when we join.
    if ([...room.remoteParticipants.values()].some(isCaller)) hooks.onRemoteConnected?.();
    return {};
  }
  /** Apply the agent's selected input/output devices to the live room. Reads the
   *  persisted settings so it works both on connect and when changed mid-call.
   *  Best-effort per device — an unavailable device must not drop the call. */
  async applyDevices(): Promise<void> {
    const room = this.room;
    if (!room) return;
    const { micDeviceId, speakerDeviceId } = loadSettings();
    if (micDeviceId) { try { await room.switchActiveDevice("audioinput", micDeviceId); } catch { /* device gone */ } }
    if (speakerDeviceId) { try { await room.switchActiveDevice("audiooutput", speakerDeviceId); } catch { /* no setSinkId */ } }
  }
  async disconnect(): Promise<void> {
    const r = this.room; this.room = null;
    /* A held flag surviving the call would silence the next one. */
    this.muted = false; this.held = false; this.audio.clear();
    try { await r?.disconnect(); } catch { /* noop */ }
  }
  private applyMic(): Promise<void> {
    const lp = this.room?.localParticipant;
    return lp ? lp.setMicrophoneEnabled(!(this.muted || this.held)).then(() => undefined) : Promise.resolve();
  }
  async setMuted(muted: boolean): Promise<void> { this.muted = muted; await this.applyMic(); }
  /** Park this end for hold: microphone closed, playback silenced. The caller's
   *  side is cut server-side (edge_media_service.legs) — hold is not music over
   *  a live two-way path. */
  async setHold(held: boolean): Promise<void> {
    this.held = held;
    for (const el of this.audio.values()) el.muted = held;
    await this.applyMic();
  }
  sendDtmf(digit: string): void {
    const code = digit === "*" ? 10 : digit === "#" ? 11 : Number(digit);
    const lp = this.room?.localParticipant as unknown as { publishDtmf?: (c: number, d: string) => Promise<void> };
    lp?.publishDtmf?.(code, digit)?.catch?.(() => undefined);
  }
}

export function makePollSource(base: string, agentId: string): PollSource {
  return {
    next: async () => {
      try {
        const r = await fetch(`${base}/next?agent_id=${encodeURIComponent(agentId)}`);
        return r.ok ? ((await r.json()) as { interaction?: InteractionRef | null }) : { interaction: null };
      } catch { return { interaction: null }; }
    },
    interactionState: async (id: string) => {
      try {
        const r = await fetch(`${base}/interaction-state?interaction_id=${encodeURIComponent(id)}`);
        return r.ok ? ((await r.json()) as { state: string | null; caller_disconnected: boolean }) : { state: null, caller_disconnected: false };
      } catch { return { state: null, caller_disconnected: false }; }
    },
  };
}
